import type { BuyerInterviewDto, BuyerInterviewRegistryDto } from "@/api/buyers";
import { useDateFormatter } from "@/utils/datetime";
import { buyerLabel } from "../buyerFilters";
import { InterviewRecord } from "./InterviewRecord";

/**
 * The print view of one completed interview: who, which version, who asked,
 * when, and the result — then the same record the drawer shows, override and
 * flags included.
 *
 * <p>Rendered into `PrintRegion` and printed by the browser; there is no PDF
 * library. It is mounted only while printing, so its rows never sit in the DOM
 * beside the drawer's own.</p>
 */
export function InterviewPrintView({
  interview,
  registry,
  buyerName,
}: {
  interview: BuyerInterviewDto;
  registry: BuyerInterviewRegistryDto;
  buyerName?: string;
}) {
  const { dateTime } = useDateFormatter();
  const outcome = interview.outcome ?? null;
  const result = outcome
    ? outcome.overriddenVerdict != null
      ? `${buyerLabel(outcome.effectiveVerdict)} (overridden from ${buyerLabel(outcome.computedVerdict)})`
      : buyerLabel(outcome.effectiveVerdict)
    : "Not recorded";

  const facts: [string, string][] = [
    ["Buyer", buyerName || "Not recorded"],
    ["Interview", `v${interview.version}`],
    ["Interviewer", interview.conductedByUserName || "Not recorded"],
    ["Completed", interview.completedAtUtc ? dateTime(interview.completedAtUtc) : "Not recorded"],
    ["Result", result],
  ];

  return (
    <div className="p-space-6 grid gap-space-4 text-onyx" data-testid="interview-print">
      <header className="grid gap-space-2">
        <h1 className="font-display font-bold text-h3 m-0">Qualification interview</h1>
        <dl className="grid gap-space-1 m-0 text-small">
          {facts.map(([label, value]) => (
            <div key={label} className="flex gap-space-2">
              <dt className="w-32 shrink-0 text-caption uppercase tracking-wide text-gray-mid">
                {label}
              </dt>
              <dd className="m-0" data-testid={`interview-print-${label.toLowerCase()}`}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </header>
      <InterviewRecord interview={interview} registry={registry} idPrefix="interview-print" />
    </div>
  );
}
